// react and js
import PropTypes from "prop-types";
import { React, useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// mui
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogTitle,
  Box,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  Collapse,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// components
import Iconify from "../../components/Iconify";
import ModalDialog from "../../components/ModalDialog";
import backendRequest from "../../components/BackendRequest";
import CardOutlet from "../../components/CardOutlet";
import LoadingAnimation from "../../components/LoadingAnimation";
import MissingContent from "../../components/MissingContent";
//
import HandleEvaluationPortalWithdraw from "./actions/HandleEvaluationPortalWithdraw";
import HandleEvaluationPortalGrant from "./actions/HandleEvaluationPortalGrant";

// hooks
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// layouts

// pages

// theme
import {
  B1Tiny,
  B3Tiny,
  FetchTableLinearProgress,
  FetchTablePagination,
  FetchTableTextField,
  FetchTableSlider,
} from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function ValueLine({ parameter, value, widthLeft, widthRight }) {
  ValueLine.propTypes = {
    parameter: PropTypes.any.isRequired,
    value: PropTypes.any,
    widthLeft: PropTypes.any.isRequired,
    widthRight: PropTypes.any.isRequired,
  };

  return (
    <Box
      sx={{
        mb: "4px",
        display: "flex",
        borderBottom: "1px solid #C8C8C8",
        borderColor: "#C8C8C8",
      }}
    >
      <Box
        sx={{
          width: widthLeft,
          pr: 1,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-end",
        }}
      >
        <Typography variant="contentTinyBold" display="block" align="left">
          {parameter}
        </Typography>
      </Box>

      <Box
        sx={{
          width: widthRight,
          pl: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Typography
          variant="contentTiny"
          display="block"
          align="right"
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function Details({ detailsData }) {
  Details.propTypes = {
    detailsData: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  return (
    <>
      <B3Tiny fullWidth variant="outlined" onClick={handleClickOpen("paper")}>
        {t("evaluationPortal.details.openButton")}
      </B3Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">{t("evaluationPortal.details.dialogTitle")}</Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
              }}
            >
              <Typography variant="contentSmall">
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.id")}
                  value={detailsData[0]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.accountNumber")}
                  value={detailsData[1]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.tradingServer")}
                  value={detailsData[2]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.accountName")}
                  value={detailsData[3]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.driveId")}
                  value={detailsData[4]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.startDatesGmt")}
                  value={detailsData[5]}
                  widthLeft="30%"
                  widthRight="70%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.startCounters")}
                  value={detailsData[6]}
                  widthLeft="30%"
                  widthRight="70%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.evaluationStart")}
                  value={detailsData[7]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.evaluationEnd")}
                  value={detailsData[8]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userIp")}
                  value={detailsData[9]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userCity")}
                  value={detailsData[10]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userState")}
                  value={detailsData[11]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userCountry")}
                  value={detailsData[12]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userCountryCode")}
                  value={detailsData[13]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userContinent")}
                  value={detailsData[14]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.userContinentCode")}
                  value={detailsData[15]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.dashboardId")}
                  value={detailsData[17]}
                  widthLeft="40%"
                  widthRight="60%"
                />
                <ValueLine
                  parameter={t("evaluationPortal.details.fields.productId")}
                  value={detailsData[18]}
                  widthLeft="40%"
                  widthRight="60%"
                />
              </Typography>
            </DialogContent>
          </>
        }
      />
    </>
  );
}

function FetchTable({ tableArray, refreshLoading }) {
  FetchTable.propTypes = {
    tableArray: PropTypes.any.isRequired,
    refreshLoading: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return isDesktop ? (
    <>
      <TableContainer component={Box} sx={{ background: "white", borderRadius: "10px 10px 13px 13px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                width="8%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold"> {t("evaluationPortal.table.id")} </Typography>
              </TableCell>
              <TableCell
                width="18%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold">
                  {t("evaluationPortal.table.accountNumber")}
                </Typography>
              </TableCell>
              <TableCell
                width="18%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold">
                  {t("evaluationPortal.table.tradingServer")}
                </Typography>
              </TableCell>
              <TableCell
                width="18%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold">
                  {t("evaluationPortal.table.dashboardId")}
                </Typography>
              </TableCell>
              <TableCell
                width="18%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold">
                  {t("evaluationPortal.table.productId")}
                </Typography>
              </TableCell>
              <TableCell
                width="15%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              />
              <TableCell
                width="15%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              />
            </TableRow>
          </TableHead>
          {refreshLoading === false ? (
            <TableBody>
              {tableArray.map((row, index) => (
                <TableRow key={index} sx={{ mb: 1 }}>
                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography variant="contentSmall"> {row[0]} </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {row[1]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {row[2]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {row[17]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {row[18]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="right"
                  >
                    {row[20] === false ? (
                      <HandleEvaluationPortalWithdraw evaluationId={row[0]}/>
                    ) : (
                      <HandleEvaluationPortalGrant evaluationId={row[0]} applicationEvaluationDays={row[19]}/>
                    )}
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="right"
                  >
                    <Details detailsData={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : null}
        </Table>
      </TableContainer>

      {refreshLoading === true ? (
        <Box
          sx={{
            justifyContent: "center",
            px: 2,
            pt: "19px",
            pb: "18px",
            background: "white",
          }}
        >
          <FetchTableLinearProgress color="inherit" sx={{ width: "100%" }} />
        </Box>
      ) : null}

      {tableArray.length === 0 && refreshLoading === false ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            px: 2,
            pt: "9px",
            pb: "9px",
            background: "white",
          }}
        >
          <Typography variant="contentSmall">
            {t("evaluationPortal.table.noDataFiltered")}
          </Typography>
        </Box>
      ) : null}
    </>
  ) : (
    <>
      {refreshLoading === false ? (
        <>
          {tableArray.map((row, index) => (
            <Stack
              key={index}
              spacing="3px"
              sx={{ background: "white", mb: 2, px: "10px", py: "10px" }}
            >
              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "50%", pr: 1 }}>
                  <Typography variant="contentSmallBold"> {t("evaluationPortal.table.id")} </Typography>
                </Box>
                <Box sx={{ width: "50%", pl: 1 }}>
                  <Typography variant="contentSmall">{row[0]}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "50%", pr: 1 }}>
                  <Typography variant="contentSmallBold">
                    {t("evaluationPortal.table.accountNumber")}
                  </Typography>
                </Box>
                <Box sx={{ width: "50%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {row[1]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "50%", pr: 1 }}>
                  <Typography variant="contentSmallBold">
                    {t("evaluationPortal.table.tradingServer")}
                  </Typography>
                </Box>
                <Box sx={{ width: "50%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {row[2]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "50%", pr: 1 }}>
                  <Typography variant="contentSmallBold">
                    {t("evaluationPortal.table.dashboardId")}
                  </Typography>
                </Box>
                <Box sx={{ width: "50%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {row[17]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "50%", pr: 1 }}>
                  <Typography variant="contentSmallBold">
                    {t("evaluationPortal.table.productId")}
                  </Typography>
                </Box>
                <Box sx={{ width: "50%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {row[18]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  {row[20] === false ? (
                    <HandleEvaluationPortalWithdraw evaluationId={row[0]} />
                  ) : (
                    <HandleEvaluationPortalGrant evaluationId={row[0]} applicationEvaluationDays={row[19]}/>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <Details detailsData={row} />
                </Box>
              </Box>
            </Stack>
          ))}
        </>
      ) : null}

      {refreshLoading === true ? (
        <Box
          sx={{
            justifyContent: "center",
            px: 2,
            pt: "19px",
            pb: "18px",
            background: "white",
          }}
        >
          <FetchTableLinearProgress color="inherit" sx={{ width: "100%" }} />
        </Box>
      ) : null}

      {tableArray.length === 0 && refreshLoading === false ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            px: 2,
            pt: "9px",
            pb: "9px",
            background: "white",
          }}
        >
          <Typography variant="contentSmall">
            {t("evaluationPortal.table.noDataFiltered")}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

function WidgetMainContentProcessed({
  content,
  error,
  warning,
  success,
  returnMessage,
  refreshLoading,
  setRefreshLoading,
  filterId,
  setFilterId,
  filterDashboardId,
  setFilterDashboardId,
  filterProductId,
  setFilterProductId,
  filterAccountNumber,
  setFilterAccountNumber,
  filterTradingServer,
  setFilterTradingServer,
}) {
  WidgetMainContentProcessed.propTypes = {
    content: PropTypes.any.isRequired,
    error: PropTypes.any.isRequired,
    warning: PropTypes.any.isRequired,
    success: PropTypes.any.isRequired,
    returnMessage: PropTypes.any.isRequired,
    refreshLoading: PropTypes.any.isRequired,
    setRefreshLoading: PropTypes.any.isRequired,
    filterId: PropTypes.any.isRequired,
    setFilterId: PropTypes.any.isRequired,
    filterDashboardId: PropTypes.any.isRequired,
    setFilterDashboardId: PropTypes.any.isRequired,
    filterProductId: PropTypes.any.isRequired,
    setFilterProductId: PropTypes.any.isRequired,
    filterAccountNumber: PropTypes.any.isRequired,
    setFilterAccountNumber: PropTypes.any.isRequired,
    filterTradingServer: PropTypes.any.isRequired,
    setFilterTradingServer: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalSettingStorage = JSON.parse(
    localStorage.getItem("myLocalSettingStorage")
  );

  let evaluationPortalTableFilterCollapseInitValue = false;
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfsc1f7fcfaa4896859 !== null &&
      myLocalSettingStorage.obfsc1f7fcfaa4896859 !== undefined
    ) {
      evaluationPortalTableFilterCollapseInitValue =
        myLocalSettingStorage.obfsc1f7fcfaa4896859;
    }
  }
  const [collapse, setCollapse] = useState(
    evaluationPortalTableFilterCollapseInitValue
  );
  useEffect(() => {
    if (
      filterId !== "" ||
      filterDashboardId !== "" ||
      filterProductId !== "" ||
      filterAccountNumber !== "" ||
      filterTradingServer !== ""
    ) {
      setCollapse(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let evaluationPortalTableFilterItemsPerPageInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfscbdd0930afb23b49 !== null &&
      myLocalSettingStorage.obfscbdd0930afb23b49 !== undefined
    ) {
      evaluationPortalTableFilterItemsPerPageInitValue =
        myLocalSettingStorage.obfscbdd0930afb23b49;
    }
  }
  const [itemsPerPages, setItemsPerPages] = useState(
    evaluationPortalTableFilterItemsPerPageInitValue
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  useEffect(() => {
    if (content.num_rows !== undefined) {
      setTotalPages(Math.ceil(content.num_rows / itemsPerPages));
      const initValueTotalPages = Math.ceil(content.num_rows / itemsPerPages);
      if (initValueTotalPages <= 0) {
        setTotalPages(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const [localFilterId, setLocalFilterId] = useState(filterId);
  const [localFilterDashboardId, setLocalFilterDashboardId] =
    useState(filterDashboardId);
  const [localFilterProductId, setLocalFilterProductId] =
    useState(filterProductId);
  const [localFilterAccountNumber, setLocalFilterAccountNumber] =
    useState(filterAccountNumber);
  const [localFilterTradingServer, setLocalFilterTradingServer] =
    useState(filterTradingServer);

  const contentFiltered = [];
  if (content.t_evaluation !== undefined) {
    content.t_evaluation.data.forEach((account, index) => {
      if (
        index >= (currentPage - 1) * itemsPerPages &&
        index <= currentPage * itemsPerPages - 1
      ) {
        contentFiltered.push(account);
      }
    });
  }

  function setFilters() {
    setCurrentPage(1);

    setFilterId(localFilterId);
    setFilterDashboardId(localFilterDashboardId);
    setFilterProductId(localFilterProductId);
    setFilterAccountNumber(localFilterAccountNumber);
    setFilterTradingServer(localFilterTradingServer);

    setRefreshLoading(true);

    setStateStorage((previousState) => ({
      ...previousState,
      widgetUpdateEvaluationPortalEvaluationPortal: true,
    }));
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12}>
        <CardOutlet
          variant="outlet"
          backgroundProp="#F2F2F2"
          title={t("evaluationPortal.card.title")}
          subheader={t("evaluationPortal.card.subtitle")}
          error={error}
          warning={warning}
          success={success}
          returnMessage={returnMessage}
          mainOutlet={
            returnMessage === t("evaluationPortal.status.noEvaluationUsers") ? null : (
              <Box>
                <B3Tiny
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                  onClick={() => {
                    setCollapse(!collapse);

                    const newLocalSettingStorage = JSON.parse(
                      localStorage.getItem("myLocalSettingStorage")
                    );

                    newLocalSettingStorage.obfsc1f7fcfaa4896859 = !collapse;
                    localStorage.setItem(
                      "myLocalSettingStorage",
                      JSON.stringify(newLocalSettingStorage)
                    );
                  }}
                >
                  {collapse ? t("evaluationPortal.filters.hide") : t("evaluationPortal.filters.show")}
                </B3Tiny>

                <Collapse
                  sx={{ mx: -1 }}
                  in={collapse}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ flexGrow: 1, mx: 1, mb: 2 }}>
                    <Grid container rowSpacing={1} columnSpacing={2}>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography variant="contentSmallBold">{t("evaluationPortal.filters.id")}</Typography>
                        <FetchTableTextField
                          value={localFilterId}
                          onChange={(event) => {
                            setLocalFilterId(event.target.value);

                            const newLocalSettingStorage = JSON.parse(
                              localStorage.getItem("myLocalSettingStorage")
                            );

                            newLocalSettingStorage.obfs21e0919001c115ad =
                              event.target.value;
                            localStorage.setItem(
                              "myLocalSettingStorage",
                              JSON.stringify(newLocalSettingStorage)
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setFilters();
                          }}
                          fullWidth
                          size="small"
                          sx={{ input: { textAlign: "center" }, mb: "10px" }}
                        />
                      </Grid>

                      <Grid item xs={6} md={6} lg={6}>
                        <Typography variant="contentSmallBold">
                          {t("evaluationPortal.filters.accountNumber")}
                        </Typography>
                        <FetchTableTextField
                          value={localFilterAccountNumber}
                          onChange={(event) => {
                            setLocalFilterAccountNumber(event.target.value);

                            const newLocalSettingStorage = JSON.parse(
                              localStorage.getItem("myLocalSettingStorage")
                            );

                            newLocalSettingStorage.obfsf89d594e2078650c =
                              event.target.value;
                            localStorage.setItem(
                              "myLocalSettingStorage",
                              JSON.stringify(newLocalSettingStorage)
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setFilters();
                          }}
                          fullWidth
                          size="small"
                          sx={{ input: { textAlign: "center" }, mb: "10px" }}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12}>
                        <Typography variant="contentSmallBold">
                          {t("evaluationPortal.filters.tradingServer")}
                        </Typography>
                        <FetchTableTextField
                          value={localFilterTradingServer}
                          onChange={(event) => {
                            setLocalFilterTradingServer(event.target.value);

                            const newLocalSettingStorage = JSON.parse(
                              localStorage.getItem("myLocalSettingStorage")
                            );

                            newLocalSettingStorage.obfs545d251b417763e1 =
                              event.target.value;
                            localStorage.setItem(
                              "myLocalSettingStorage",
                              JSON.stringify(newLocalSettingStorage)
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setFilters();
                          }}
                          fullWidth
                          size="small"
                          sx={{ input: { textAlign: "center" }, mb: "10px" }}
                        />
                      </Grid>

                      <Grid item xs={6} md={6} lg={6}>
                        <Typography variant="contentSmallBold">
                          {t("evaluationPortal.filters.dashboardId")}
                        </Typography>
                        <FetchTableTextField
                          value={localFilterDashboardId}
                          onChange={(event) => {
                            setLocalFilterDashboardId(event.target.value);

                            const newLocalSettingStorage = JSON.parse(
                              localStorage.getItem("myLocalSettingStorage")
                            );

                            newLocalSettingStorage.obfs7c4d8a1f9e3b250a =
                              event.target.value;
                            localStorage.setItem(
                              "myLocalSettingStorage",
                              JSON.stringify(newLocalSettingStorage)
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setFilters();
                          }}
                          fullWidth
                          size="small"
                          sx={{ input: { textAlign: "center" }, mb: "10px" }}
                        />
                      </Grid>

                      <Grid item xs={6} md={6} lg={6}>
                        <Typography variant="contentSmallBold">
                          {t("evaluationPortal.filters.productId")}
                        </Typography>
                        <FetchTableTextField
                          value={localFilterProductId}
                          onChange={(event) => {
                            setLocalFilterProductId(event.target.value);

                            const newLocalSettingStorage = JSON.parse(
                              localStorage.getItem("myLocalSettingStorage")
                            );

                            newLocalSettingStorage.obfs6e2a3b8d1f9c4705 =
                              event.target.value;
                            localStorage.setItem(
                              "myLocalSettingStorage",
                              JSON.stringify(newLocalSettingStorage)
                            );
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setFilters();
                          }}
                          fullWidth
                          size="small"
                          sx={{ input: { textAlign: "center" }, mb: "10px" }}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12}>
                        <Typography variant="contentSmallBold">
                          {t("evaluationPortal.filters.itemsPerPage")}
                        </Typography>
                        <Box sx={{ px: 1 }}>
                          <FetchTableSlider
                            size="small"
                            min={1}
                            max={100}
                            align="right"
                            value={itemsPerPages}
                            onChange={(event, newValue) => {
                              setItemsPerPages(newValue);
                              setTotalPages(
                                Math.ceil(content.num_rows / newValue)
                              );
                              setCurrentPage(1);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfscbdd0930afb23b49 =
                                newValue;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            valueLabelDisplay="auto"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <B1Tiny
                      fullWidth
                      variant="contained"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setFilters();
                      }}
                    >
                      {t("evaluationPortal.filters.applyButton")}
                    </B1Tiny>
                  </Box>
                </Collapse>

                <Box sx={{ mt: 0, mb: 1 }} />

                <FetchTable
                  tableArray={contentFiltered}
                  refreshLoading={refreshLoading}
                />

                <Stack sx={{ mt: 3 }} alignItems="center">
                  <FetchTablePagination
                    count={totalPages}
                    variant="outlined"
                    shape="rounded"
                    page={currentPage}
                    onChange={(event, value) => {
                      setCurrentPage(value);
                    }}
                  />
                </Stack>
              </Box>
            )
          }
        />
      </Grid>
    </Grid>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget() {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  Widget.propTypes = {};

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalSettingStorage = JSON.parse(
    localStorage.getItem("myLocalSettingStorage")
  );

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [loading, setLoading] = useState(navigator.onLine);
  const [content, setContent] = useState();
  const [returnMessage, setReturnMessage] = useState("");

  const [regularRefreshIntervalCounter, setRegularRefreshIntervalCounter] =
    useState(0);

  // Filter
  let evaluationPortalTableFilterIdInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs21e0919001c115ad !== null &&
      myLocalSettingStorage.obfs21e0919001c115ad !== undefined
    ) {
      evaluationPortalTableFilterIdInitValue =
        myLocalSettingStorage.obfs21e0919001c115ad;
    }
  }
  const [filterId, setFilterId] = useState(
    evaluationPortalTableFilterIdInitValue
  );

  // Filter
  let evaluationPortalTableFilterDashboardIdInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs7c4d8a1f9e3b250a !== null &&
      myLocalSettingStorage.obfs7c4d8a1f9e3b250a !== undefined
    ) {
      evaluationPortalTableFilterDashboardIdInitValue =
        myLocalSettingStorage.obfs7c4d8a1f9e3b250a;
    }
  }
  const [filterDashboardId, setFilterDashboardId] = useState(
    evaluationPortalTableFilterDashboardIdInitValue
  );

  // Filter
  let evaluationPortalTableFilterProductIdInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs6e2a3b8d1f9c4705 !== null &&
      myLocalSettingStorage.obfs6e2a3b8d1f9c4705 !== undefined
    ) {
      evaluationPortalTableFilterProductIdInitValue =
        myLocalSettingStorage.obfs6e2a3b8d1f9c4705;
    }
  }
  const [filterProductId, setFilterProductId] = useState(
    evaluationPortalTableFilterProductIdInitValue
  );

  // Filter
  let evaluationPortalTableFilterAccountNumberInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfsf89d594e2078650c !== null &&
      myLocalSettingStorage.obfsf89d594e2078650c !== undefined
    ) {
      evaluationPortalTableFilterAccountNumberInitValue =
        myLocalSettingStorage.obfsf89d594e2078650c;
    }
  }
  const [filterAccountNumber, setFilterAccountNumber] = useState(
    evaluationPortalTableFilterAccountNumberInitValue
  );

  // Filter
  let evaluationPortalTableFilterTradingServerInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs545d251b417763e1 !== null &&
      myLocalSettingStorage.obfs545d251b417763e1 !== undefined
    ) {
      evaluationPortalTableFilterTradingServerInitValue =
        myLocalSettingStorage.obfs545d251b417763e1;
    }
  }
  const [filterTradingServer, setFilterTradingServer] = useState(
    evaluationPortalTableFilterTradingServerInitValue
  );

  const [refreshLoading, setRefreshLoading] = useState(false);

  // ######################################################
  // Main backend request
  // ######################################################
  function MainRequest(periodic) {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      if (periodic === false) {
        setStateStorage((previousState) => ({
          ...previousState,
          widgetUpdateEvaluationPortalEvaluationPortal: false,
        }));

        setError(false);
        setWarning(false);
        setSuccess(false);
        setReturnMessage("");
      }

      const sendData = {
        request_type: "dashboard",
        route_info: "evaluation_portal",
        operation: "fetch",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
        selection: {
          page_start: "",
          page_count: "",
          page_size: "",
          page_order_type: "desc",
          page_order_by: "evaluation_id",
        },
        filters: {
          evaluation_id: filterId,
          dashboard_id: filterDashboardId,
          product_id: filterProductId,
          account_number: filterAccountNumber,
          account_server: filterTradingServer,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("message");
          if (checkKey === true) {
            if (contentFromBackend.message === "success") {
              setError(false);
              setLoading(false);
              setRefreshLoading(false);
              setContent(contentFromBackend);

              if (contentFromBackend.num_rows === 0) {
                if (
                  filterId === "" &&
                  filterDashboardId === "" &&
                  filterProductId === "" &&
                  filterAccountNumber === "" &&
                  filterTradingServer === ""
                ) {
                  setSuccess(true);
                  setReturnMessage(<TranslatedMessage tKey="evaluationPortal.status.noEvaluationUsers" />);
                } else {
                  setReturnMessage("empty_filtered");
                }
              } else {
                setSuccess(false);
                setReturnMessage("");
              }
            } else if (contentFromBackend.message === "token invalid") {
              setWarning(true);
              setLoading(false);
              setRefreshLoading(false);
              setReturnMessage(<TranslatedMessage tKey="evaluationPortal.status.loggedOut" />);

              setStateStorage(stateStorageInit);
              localStorage.setItem("forcedLoggedOut", true);

              localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalStateStorage");

              sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
              sessionStorage.removeItem("myLocalLastRoute");

              localStorage.setItem("myLocalSettingStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalSettingStorage");

              localStorage.setItem("myReauthentication", JSON.stringify(""));
              localStorage.removeItem("myReauthentication");
            } else {
              setError(true);
              setLoading(false);
              setRefreshLoading(false);
              setReturnMessage(<TranslatedMessage tKey="evaluationPortal.status.invalidRequest" />);
            }
          } else {
            setError(true);
            setLoading(false);
            setRefreshLoading(false);
            setReturnMessage(<TranslatedMessage tKey="evaluationPortal.status.unknownError" />);
          }
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setError(true);
            setLoading(false);
            setRefreshLoading(false);
            setReturnMessage(<TranslatedMessage tKey="evaluationPortal.status.unknownError" />);
          }
        });
    }
  }

  // ######################################################
  // Inital and triggered refreshs
  // ######################################################
  useEffect(() => {
    if (!stateStorage.widgetUpdateEvaluationPortalEvaluationPortal) return;

    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateStorage.widgetUpdateEvaluationPortalEvaluationPortal]);

  // ######################################################
  // Regular refreshs
  // ######################################################
  useEffect(() => {
    const timerID = setTimeout(() => {
      setRegularRefreshIntervalCounter(regularRefreshIntervalCounter + 1);
    }, stateStorage.widgetUpdateIntervalEvaluationPortalEvaluationPortal);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  useEffect(() => {
    MainRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  // ######################################################
  // Data representation
  // ######################################################
  let WidgetOutlet = <MissingContent message={t("evaluationPortal.card.title")} />;
  if (loading) {
    WidgetOutlet = <LoadingAnimation />;
  } else if (error) {
    WidgetOutlet = <MissingContent message={t("evaluationPortal.card.title")} />;
  } else if (content) {
    WidgetOutlet = (
      <WidgetMainContentProcessed
        content={content}
        error={error}
        warning={warning}
        success={success}
        returnMessage={returnMessage}
        refreshLoading={refreshLoading}
        setRefreshLoading={setRefreshLoading}
        filterId={filterId}
        setFilterId={setFilterId}
        filterDashboardId={filterDashboardId}
        setFilterDashboardId={setFilterDashboardId}
        filterProductId={filterProductId}
        setFilterProductId={setFilterProductId}
        filterAccountNumber={filterAccountNumber}
        setFilterAccountNumber={setFilterAccountNumber}
        filterTradingServer={filterTradingServer}
        setFilterTradingServer={setFilterTradingServer}
      />
    );
  }

  // ######################################################
  // Outlet
  // ######################################################
  return <> {WidgetOutlet} </>;
}
